import { ParsedUrlQuery } from "querystring"
import { useEffect, useRef } from "react"
import { Session } from "@/types/auth"
import { Organization } from "@/types/organization"
import { getSessionId } from "@amplitude/analytics-browser"

import {
  AmplitudeInitProps,
  getUtmParams,
  initAmplitude,
  setAnalyticsUserProperties,
  updateAmplitudeSessionId,
} from "@/lib/analytics/amplitude-utils"
import { isClientSide } from "@/lib/is-client-side"
import { singleParam } from "@/lib/utils/url"
import { webStorage } from "@/lib/web-storage"

interface UseAmplitudeInitProps {
  session?: Session
  organization?: Organization
  pathname: string
  query: ParsedUrlQuery
}

/**
 * Hook to initialize Amplitude analytics and associate session IDs
 * Shared between WebApp and MinimalApp
 */
export function useAmplitudeInit({
  session,
  organization,
  pathname,
  query,
}: UseAmplitudeInitProps) {
  // Initialize amplitude early on
  if (isClientSide()) {
    const uuid = session?.user?.uuid
    const provider = session?.user?.provider
    const handle = session?.user?.handle
    const orgid = organization?.orgid
    const status =
      uuid && provider !== "anonymous" && handle
        ? "authenticated"
        : "unauthenticated"
    const has_thatch_data = session?.user?.has_thatch_data
    const is_from_thatch = session?.user?.is_from_thatch
    const is_thatch_creator = session?.user?.is_thatch_creator
    const is_crawler = session?.user?.is_crawler

    // For create-anon and refresh pages, don't initialize Amplitude. Wait until the reload or redirect.
    // And then we will initialize Amplitude in the final destination page.
    // Also get the referer that we stored from /create-anon
    let referer = undefined
    const skipAmplitudeInit =
      pathname.includes("/create-anon") || pathname.includes("/auth/refresh")
    if (!skipAmplitudeInit) {
      try {
        referer =
          webStorage.getItem("lastAmplitudeReferer", { type: "session" }) ||
          undefined

        const initProps: AmplitudeInitProps = {
          type: "initProps",
          uuid: uuid,
          provider: provider,
          handle: handle,
          status: status,
          orgid: orgid,
          permissions: session?.permissions,
          has_thatch_data: has_thatch_data,
          is_from_thatch: is_from_thatch,
          is_thatch_creator: is_thatch_creator,
          is_crawler: is_crawler,
          thatch_redirect: !!singleParam(query.tm),
          referer: referer,
          mt_referer: referer,
        }

        // Initialize amplitude
        initAmplitude(initProps)

        // Sometime Amplitude does not pick up the utm_* params.  Force set them.
        // I noticed this when I'm doing the Thatch signup
        const utmProperties = getUtmParams(query)
        setAnalyticsUserProperties(utmProperties)
      } finally {
        webStorage.removeItem("lastAmplitudeReferer", { type: "session" })
      }
    }
  }

  // Associate sessionId and uuid
  const initAmplitudeState = useRef(false)
  useEffect(() => {
    const amplitudeSessionId = getSessionId()

    if (!initAmplitudeState.current) {
      if (amplitudeSessionId !== undefined && session?.user?.uuid) {
        initAmplitudeState.current = true
        void updateAmplitudeSessionId(amplitudeSessionId, session?.user?.uuid)
      }
    }
  }, [
    session?.user?.uuid,
    session?.user?.provider,
    session?.user?.handle,
    organization?.orgid,
    session?.permissions,
    session?.user?.has_thatch_data,
    session?.user?.is_from_thatch,
    session?.user?.is_thatch_creator,
    pathname,
    query,
  ])
}
